import { selectUserInfo } from "features/user/userSlice";
import { Box, Grid, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { brandingInfo } from "features/server/brandingSlice";
import { BrandingResponse } from "subModule/src/services/allcommon/interface";
import { themes } from "theme/themes";
import { useSnackbar } from "notistack";
import AccountsExpenses from "components/AccountsSummary/AccountsExpenses";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import { useTranslation } from "react-i18next";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import AccountsIncome from "components/AccountsSummary/AccountsIncome";
import AccountsAssets from "components/AccountsSummary/AccountsAssets";
import AccountsLiabilities from "components/AccountsSummary/AccountsLiabilities";
import AccountsEquities from "components/AccountsSummary/AccountsEquities";
import AccountsProfitLoss from "components/AccountsSummary/AccountsProfitLoss";
import { Controller, useForm } from "react-hook-form";
import dayjs from "dayjs";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import DateRangePicker from "common/Pickers/DateRangePicker";
export default function AccountsSummary() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const loggedInUserData = useSelector(selectUserInfo);
  const branding = useSelector(brandingInfo);
  const themeColors =
    (branding as BrandingResponse)?.attributes?.themeId === 100
      ? (branding as BrandingResponse)?.attributes?.customTheme
      : (themes as any)[
          (branding as BrandingResponse)?.attributes?.themeId || 3
        ];
  const { control, setValue, trigger, watch } = useForm<{ [key: string]: any }>(
    {
      defaultValues: {},
    },
  );
  const [bsloading, setBsLoading] = useState<boolean>(true);
  const [balanceSheetFilters, setBalanceSheetFilters] =
    useState<boolean>(false);
  const [profitLossFilters, setProfitLossFilters] = useState<boolean>(false);
  const [plloading, setPLLoading] = useState<boolean>(true);
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <ComponentHeaderTwo
          showBtn={false}
          showSaveBtn={false}
          heading={t("accountsSummary")}
          icon={
            <AccountBalanceWalletOutlinedIcon
              sx={{
                fill: "#000",
                width: "30px",
                height: "30px",
                marginTop: "3px",
              }}
            />
          }
          btnText="back"
          btnType="back"
          isBtnEnabled={false}
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box sx={{ textAlign: "left" }}>
            <Typography
              variant="h4"
              className="font-Saira text-start"
              sx={{
                fontSize: { xs: 14, sm: 17 },
                fontWeight: { xs: 700, sm: 800 },
              }}
            >
              {t("balanceSheet")}
            </Typography>
            <Typography className="font-Saira text-start text-[13px] mb-[5px] italic">
              - - - - - - - -
            </Typography>
          </Box>
          <Box
            className="flex gap-1 items-center"
            sx={{
              mt: { xs: 0, sm: "-10px" },
              width: { xs: "100%", sm: "auto" },
              justifyContent: { xs: "flex-start", sm: "flex-end" },
              flexWrap: "wrap",
            }}
          >
            <Typography
              className="font-Saira text-[11px] italic px-[8px] font-medium"
              sx={{ color: themeColors.primary }}
            >
              {t("dateInterval")}
            </Typography>
            <Controller
              name="dateRange"
              control={control}
              rules={{ required: false }}
              render={({ field }) => (
                <Box
                  sx={{
                    "& .MuiOutlinedInput-root": { height: "30px" },
                    "& .MuiInputBase-input": { fontSize: "12px !important" },
                    "& .clearButton svg": {
                      width: "18px",
                      height: "18px",
                      background: `${themeColors.primary}10`,
                      padding: "3px",
                      borderRadius: "50%",
                    },
                  }}
                >
                  <DateRangePicker
                    {...field}
                    label=""
                    maxDate={dayjs()}
                    placeholder={"YYYY-MM-DD → YYYY-MM-DD"}
                    onChange={(range: any) => {
                      field.onChange(range);
                      setValue("start", range[0]);
                      setValue("end", range[1]);
                      trigger("dateRange");
                      if (
                        (watch("start") && watch("end")) ||
                        (watch("start") === null && watch("end") === null)
                      ) {
                        setBalanceSheetFilters(true);
                      }
                    }}
                    value={[
                      watch("start") ? dayjs(watch("start")) : null,
                      watch("end") ? dayjs(watch("end")) : null,
                    ]}
                    shortcuts={[
                      { label: t("today"), getValue: () => [dayjs(), dayjs()] },
                      {
                        label: t("yesterday"),
                        getValue: () => [
                          dayjs().subtract(1, "day"),
                          dayjs().subtract(1, "day"),
                        ],
                      },
                      {
                        label: t("thisWeek"),
                        getValue: () => [dayjs().startOf("week"), dayjs()],
                      },
                      {
                        label: t("lastWeek"),
                        getValue: () => [
                          dayjs().subtract(1, "week").startOf("week"),
                          dayjs().subtract(1, "week").endOf("week"),
                        ],
                      },
                      {
                        label: t("thisMonth"),
                        getValue: () => [dayjs().startOf("month"), dayjs()],
                      },
                      {
                        label: t("lastMonth"),
                        getValue: () => [
                          dayjs().subtract(1, "month").startOf("month"),
                          dayjs().subtract(1, "month").endOf("month"),
                        ],
                      },
                      {
                        label: t("thisYear"),
                        getValue: () => [dayjs().startOf("year"), dayjs()],
                      },
                      {
                        label: t("lastYear"),
                        getValue: () => [
                          dayjs().subtract(1, "year").startOf("year"),
                          dayjs().subtract(1, "year").endOf("year"),
                        ],
                      },
                      {
                        label: t("customDateRange"),
                        getValue: () => [null, null],
                      },
                    ]}
                    disableDateRange={bsloading}
                  />
                </Box>
              )}
            />
          </Box>
        </Box>
        <AccountsAssets
          user={loggedInUserData}
          themes={themeColors}
          startDate={watch("start")?.format("YYYY-MM-DD") ?? null}
          endDate={watch("end")?.format("YYYY-MM-DD") ?? null}
          {...{
            enqueueSnackbar,
            setBsLoading,
            balanceSheetFilters,
            setBalanceSheetFilters,
            navigate,
          }}
        />
        <Box className="h-[16px]"></Box>
        <AccountsLiabilities
          user={loggedInUserData}
          themes={themeColors}
          startDate={watch("start")?.format("YYYY-MM-DD") ?? null}
          endDate={watch("end")?.format("YYYY-MM-DD") ?? null}
          {...{
            enqueueSnackbar,
            setBsLoading,
            balanceSheetFilters,
            setBalanceSheetFilters,
          }}
        />
        <Box className="h-[16px]"></Box>
        <AccountsEquities
          user={loggedInUserData}
          themes={themeColors}
          startDate={watch("start")?.format("YYYY-MM-DD") ?? null}
          endDate={watch("end")?.format("YYYY-MM-DD") ?? null}
          {...{
            enqueueSnackbar,
            setBsLoading,
            balanceSheetFilters,
            setBalanceSheetFilters,
          }}
        />
      </Grid>
      <Grid item xs={12} lg={6}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          <Box sx={{ textAlign: "left" }}>
            <Typography
              variant="h4"
              className="font-Saira text-start"
              sx={{
                fontSize: { xs: 14, sm: 17 },
                fontWeight: { xs: 700, sm: 800 },
              }}
            >
              {t("profitLossStatement")}
            </Typography>
            <Typography className="font-Saira text-start text-[13px] mb-[5px] italic">
              - - - - - - - -
            </Typography>
          </Box>
          <Box
            className="flex gap-1 items-center"
            sx={{
              mt: { xs: 0, sm: "-10px" },
              width: { xs: "100%", sm: "auto" },
              justifyContent: { xs: "flex-start", sm: "flex-end" },
              flexWrap: "wrap",
            }}
          >
            <Typography
              className="font-Saira text-[11px] italic px-[8px] font-medium"
              sx={{ color: themeColors.primary }}
            >
              {t("dateInterval")}
            </Typography>
            <Controller
              name="dateRange1"
              control={control}
              rules={{ required: false }}
              render={({ field }) => (
                <Box
                  sx={{
                    "& .MuiOutlinedInput-root": { height: "30px" },
                    "& .MuiInputBase-input": { fontSize: "12px !important" },
                    "& .clearButton svg": {
                      width: "18px",
                      height: "18px",
                      background: `${themeColors.primary}10`,
                      padding: "3px",
                      borderRadius: "50%",
                    },
                  }}
                >
                  <DateRangePicker
                    {...field}
                    label=""
                    maxDate={dayjs()}
                    placeholder={"YYYY-MM-DD → YYYY-MM-DD"}
                    onChange={(range: any) => {
                      field.onChange(range);
                      setValue("start1", range[0]);
                      setValue("end1", range[1]);
                      trigger("dateRange1");
                      if (
                        (watch("start1") && watch("end1")) ||
                        (watch("start1") === null && watch("end1") === null)
                      ) {
                        setProfitLossFilters(true);
                      }
                    }}
                    value={[
                      watch("start1") ? dayjs(watch("start1")) : null,
                      watch("end1") ? dayjs(watch("end1")) : null,
                    ]}
                    shortcuts={[
                      { label: t("today"), getValue: () => [dayjs(), dayjs()] },
                      {
                        label: t("yesterday"),
                        getValue: () => [
                          dayjs().subtract(1, "day"),
                          dayjs().subtract(1, "day"),
                        ],
                      },
                      {
                        label: t("thisWeek"),
                        getValue: () => [dayjs().startOf("week"), dayjs()],
                      },
                      {
                        label: t("lastWeek"),
                        getValue: () => [
                          dayjs().subtract(1, "week").startOf("week"),
                          dayjs().subtract(1, "week").endOf("week"),
                        ],
                      },
                      {
                        label: t("thisMonth"),
                        getValue: () => [dayjs().startOf("month"), dayjs()],
                      },
                      {
                        label: t("lastMonth"),
                        getValue: () => [
                          dayjs().subtract(1, "month").startOf("month"),
                          dayjs().subtract(1, "month").endOf("month"),
                        ],
                      },
                      {
                        label: t("thisYear"),
                        getValue: () => [dayjs().startOf("year"), dayjs()],
                      },
                      {
                        label: t("lastYear"),
                        getValue: () => [
                          dayjs().subtract(1, "year").startOf("year"),
                          dayjs().subtract(1, "year").endOf("year"),
                        ],
                      },
                      {
                        label: t("customDateRange"),
                        getValue: () => [null, null],
                      },
                    ]}
                    disableDateRange={plloading}
                  />
                </Box>
              )}
            />
          </Box>
        </Box>
        <AccountsIncome
          user={loggedInUserData}
          themes={themeColors}
          startDate={watch("start1")?.format("YYYY-MM-DD") ?? null}
          endDate={watch("end1")?.format("YYYY-MM-DD") ?? null}
          {...{
            enqueueSnackbar,
            setPLLoading,
            profitLossFilters,
            setProfitLossFilters,
          }}
        />
        <Box className="h-[16px]"></Box>
        <AccountsExpenses
          user={loggedInUserData}
          themes={themeColors}
          startDate={watch("start1")?.format("YYYY-MM-DD") ?? null}
          endDate={watch("end1")?.format("YYYY-MM-DD") ?? null}
          {...{
            enqueueSnackbar,
            setPLLoading,
            profitLossFilters,
            setProfitLossFilters,
          }}
        />
        <Box className="h-[16px]"></Box>
        <AccountsProfitLoss
          user={loggedInUserData}
          themes={themeColors}
          startDate={watch("start1")?.format("YYYY-MM-DD") ?? null}
          endDate={watch("end1")?.format("YYYY-MM-DD") ?? null}
          {...{
            enqueueSnackbar,
            setPLLoading,
            profitLossFilters,
            setProfitLossFilters,
          }}
        />
      </Grid>
    </Grid>
  );
}
